import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import InputMask from 'react-input-mask';
import axios from 'axios';
import { PlayerCardOperator } from '../../types';
import { playerCardOperators } from '..';
import styled from 'styled-components';
import { RewardsCardInformationComponent } from './rewards-card-information-component';
import RewardsCardImage from '../../assets/images/rewards-card';
import Input from '../../components/input';
import { BasicKeypad } from '../../components/keypad/popup-keypad';
import Button from '../../components/button';
import { closeModal } from '../../components/modal';
import { Block, Text } from '../../components/typography';
import { IStoreState } from '../../store/types';

type EnterAddCardNumberModalComponentProps = {
  kioskAnonymousLimitAmount?: string;
  cardOperator: PlayerCardOperator;
  isSearching: boolean;
  isManualEntryEnabled?: boolean;
  isMaximumCashDepositExceeded?: boolean;
  playerCardProvider: string;
  deviceType: string;
  initialValue?: string;
  searchForCardNumber(cardNumber: string): void;
  onCancel?: () => void;
};

export const EnterAddCardNumberModalComponent = ({
  cardOperator,
  isSearching,
  kioskAnonymousLimitAmount,
  isMaximumCashDepositExceeded,
  playerCardProvider,
  deviceType,
  isManualEntryEnabled,
  initialValue,
  searchForCardNumber,
  onCancel
}: EnterAddCardNumberModalComponentProps) => {
  const [cardNumber, setCardNumber] = useState<string>(initialValue || '');
  const numberFormat = playerCardOperators[cardOperator].cardNumberFormat;
  const isCaesarsRewards = cardOperator === 'CAESARS';
  const terminalUrls = useSelector(({ config }: ReturnType<IStoreState>) => config.URLS.terminal);

  const callbackRef = useCallback(inputElement => {
    if (inputElement) {
      inputElement.focus();
    }
  }, []);

  const notifyAndSearch = cardNumber => {
    if ('KIOSK' === deviceType) {
      // Here to maintain consistency with the physical scan journey
      axios.post(`${terminalUrls.base}/${terminalUrls.eventNotification}`, { eventType: 'CARD_READ' });
    }
    searchForCardNumber(cardNumber);
  };

  return (
    <ModalContainer className={`${classNamePrefix}container`}>
      {kioskAnonymousLimitAmount && (
        <RewardsHeading
          {...{ isCaesarsRewards, kioskAnonymousLimitAmount, isManualEntryEnabled, isMaximumCashDepositExceeded }}
        />
      )}
      <RewardsCardInformationComponent {...{ isManualEntryEnabled, playerCardProvider }} />
      <RewardsCardActionsComponent>
        <RewardsCardComponent>
          {isManualEntryEnabled ? (
            <>
              <RewardsCardInputWrapper>
                <Text weight="bold" textAlign="left">
                  Enter Rewards Card Number
                </Text>
                <InputMask mask={numberFormat.mask} maskChar="" value={cardNumber}>
                  {() => (
                    <InputWrapper
                      className={`${classNamePrefix}enter-card-number`}
                      componentsize="standard"
                      value={cardNumber}
                      labeltext={<RewardsCardImage />}
                      placeholder="eg: 123456789"
                      ref={callbackRef}
                    />
                  )}
                </InputMask>
              </RewardsCardInputWrapper>
              <KeypadWrapper>
                <BasicKeypad
                  existingValue={cardNumber}
                  maxValue={Number('9'.repeat(numberFormat.max))}
                  onKeyPress={setCardNumber}
                />
              </KeypadWrapper>
              <Buttons>
                <Button
                  buttonSize="standard"
                  className={`${classNamePrefix}cancel`}
                  modifiers="secondaryRound"
                  onClick={() => {
                    closeModal();
                    onCancel?.();
                  }}
                  text="Cancel"
                />
                <Button
                  buttonSize="standard"
                  className={`${classNamePrefix}search`}
                  disabled={cardNumber.length < numberFormat.min || isSearching}
                  modifiers="primaryRound"
                  onClick={() => notifyAndSearch(cardNumber)}
                  text="Search"
                />
              </Buttons>
            </>
          ) : (
            <Button
              buttonSize="standard"
              className={`${classNamePrefix}cancel`}
              modifiers="secondaryRound"
              onClick={() => {
                closeModal();
              }}
              text="Close"
            />
          )}
        </RewardsCardComponent>
      </RewardsCardActionsComponent>
    </ModalContainer>
  );
};

const classNamePrefix = 'qa-player-card-modal-';

const ModalContainer = styled.div`
  width: 82.4rem;
`;

const KeypadWrapper = styled.div`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  box-shadow: ${({ theme }) => theme.shadows.drop};
  display: flex;
  left: 29rem;
  padding: 1.2rem;
  position: absolute;
  top: 0rem;
  z-index: 1000;
  width: 27.6rem;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2rem;
  width: 100%;
  & :first-child {
    margin-right: 1rem;
  }
`;

const RewardsCardActionsComponent = styled.div`
  display: flex;
  justify-content: center;
`;

const RewardsCardComponent = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
`;

const RewardsCardInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  > * + * {
    margin-top: 1rem;
  }
`;

const InputWrapper = styled(Input)`
  caret-color: transparent;
`;

const RewardsHeading = ({
  isCaesarsRewards,
  kioskAnonymousLimitAmount,
  isManualEntryEnabled,
  isMaximumCashDepositExceeded
}: {
  isCaesarsRewards?: boolean;
  kioskAnonymousLimitAmount: string;
  isManualEntryEnabled?: boolean;
  isMaximumCashDepositExceeded?: boolean;
}) => {
  return (
    <FlexCenteredRow>
      {isMaximumCashDepositExceeded ? (
        <MaximumCashDepositExceededMessage {...{ isCaesarsRewards, isManualEntryEnabled, kioskAnonymousLimitAmount }} />
      ) : (
        <TotalWagerExceededMessage {...{ isCaesarsRewards, isManualEntryEnabled, kioskAnonymousLimitAmount }} />
      )}
    </FlexCenteredRow>
  );
};

const FlexCenteredRow = styled.div`
  display: flex;
  justify-content: center;
`;

const RewardsHeadingWrapper = styled(Block)`
  width: 50rem;
  margin-top: 2rem;
  margin-bottom: 1rem;
`;

const RedText = styled(Text)`
  color: ${({ theme }) => theme.colors.red};
`;

const MaximumCashDepositExceededMessage = ({
  isCaesarsRewards,
  isManualEntryEnabled,
  kioskAnonymousLimitAmount
}: {
  isCaesarsRewards?: boolean;
  kioskAnonymousLimitAmount: string;
  isManualEntryEnabled?: boolean;
}) => {
  return (
    <RewardsHeadingWrapper textAlign="center" weight="bold">
      You have tried to exceed the maximum cash deposit of <RedText>{kioskAnonymousLimitAmount}</RedText> for this
      session.{' '}
      {isManualEntryEnabled
        ? `Enter your ${isCaesarsRewards ? `Caesars Rewards®` : 'Rewards'} card number to continue adding cash.`
        : `Insert your ${isCaesarsRewards ? `Caesars Rewards®` : 'Rewards'}  card to continue adding cash.`}
    </RewardsHeadingWrapper>
  );
};

const TotalWagerExceededMessage = ({
  isCaesarsRewards,
  isManualEntryEnabled,
  kioskAnonymousLimitAmount
}: {
  isCaesarsRewards?: boolean;
  kioskAnonymousLimitAmount: string;
  isManualEntryEnabled?: boolean;
}) => {
  return (
    <RewardsHeadingWrapper textAlign="center" weight="bold">
      A total wager above <RedText>{kioskAnonymousLimitAmount}</RedText> on a Kiosk requires you to{' '}
      {`${
        isManualEntryEnabled
          ? `enter your ${isCaesarsRewards ? `Caesars Rewards®` : 'Rewards'} card number.`
          : 'insert your Rewards card.'
      }`}
    </RewardsHeadingWrapper>
  );
};
